import { Injectable } from '@angular/core';
import { UserConfigServices } from './user-config.services';
import { NzThemeBridgeService } from './nz-theme-bridge.service';
import { ObjectUtil } from '../system-utils/utils/ObjectUtil';

/**
 * Tema personalizado por usuário (layout 'custom').
 *
 * A paleta é um conjunto de valores para os tokens --tem-* do temapi-theme,
 * salva em MKTPUSERPREF (idpreference 'user_custom_theme'). A aplicação é
 * feita gerando em runtime uma classe `.custom-theme` num <style> no <head>:
 * como a tag entra DEPOIS do CSS compilado, ela vence os temas fixos
 * (.modern-theme/.warm-theme/.violet-theme/.orange-theme/.vault-theme/.brasil-theme/.zorro-theme/.bootstrap-theme) na cascata,
 * exatamente como o VAULT vence o dark por ordem de declaração. O system-view-new aplica a classe no
 * nz-layout e no OverlayContainer quando o layout do usuário é 'custom'.
 */

export interface CustomThemePalette {
    base: 'light' | 'dark';
    colors: { [token: string]: string };
}

export interface CustomThemeToken {
    token: string;
    labelKey: string;
}

const STYLE_TAG_ID = 'tem-custom-theme';
export const CUSTOM_THEME_PREFERENCE_ID = 'user_custom_theme';

@Injectable({ providedIn: 'root' })
export class CustomThemeService {

    constructor(private ucs: UserConfigServices, private nzBridge: NzThemeBridgeService) { }

    /** Tokens editáveis pelo usuário, na ordem de exibição do editor. */
    readonly tokens: CustomThemeToken[] = [
        { token: '--tem-bg',          labelKey: 'systemView.customTheme.bg' },
        { token: '--tem-surface',     labelKey: 'systemView.customTheme.surface' },
        { token: '--tem-surface-2',   labelKey: 'systemView.customTheme.surface2' },
        { token: '--tem-chrome',      labelKey: 'systemView.customTheme.chrome' },
        { token: '--tem-border',      labelKey: 'systemView.customTheme.border' },
        { token: '--tem-ink',         labelKey: 'systemView.customTheme.ink' },
        { token: '--tem-muted',       labelKey: 'systemView.customTheme.muted' },
        { token: '--tem-deep',        labelKey: 'systemView.customTheme.deep' },
        { token: '--tem-deep-strong', labelKey: 'systemView.customTheme.deepStrong' },
        { token: '--tem-accent',      labelKey: 'systemView.customTheme.accent' },
        { token: '--tem-danger',      labelKey: 'systemView.customTheme.danger' },
        { token: '--tem-warn',        labelKey: 'systemView.customTheme.warn' },
        { token: '--tem-success',     labelKey: 'systemView.customTheme.success' },
    ];

    /** Paletas de partida — literais espelhando o temapi-theme.scss. */
    defaults(base: 'light' | 'dark'): CustomThemePalette {
        if (base === 'dark') {
            return {
                base: 'dark',
                colors: {
                    '--tem-bg': '#101a22',
                    '--tem-surface': '#16222c',
                    '--tem-surface-2': '#1d2b37',
                    '--tem-chrome': '#16222c',
                    '--tem-border': '#33475a',
                    '--tem-ink': '#dce8f2',
                    '--tem-muted': '#93a8ba',
                    '--tem-deep': '#3d7fae',
                    '--tem-deep-strong': '#0b2940',
                    '--tem-accent': '#35d9b4',
                    '--tem-danger': '#e57373',
                    '--tem-warn': '#f0b95a',
                    '--tem-success': '#4dbd8b',
                }
            };
        }

        return {
            base: 'light',
            colors: {
                '--tem-bg': '#eef4f8',
                '--tem-surface': '#ffffff',
                '--tem-surface-2': '#f2f7fa',
                '--tem-chrome': '#e7f1f7',
                '--tem-border': '#d7e3ea',
                '--tem-ink': '#16354c',
                '--tem-muted': '#587a8f',
                '--tem-deep': '#134465',
                '--tem-deep-strong': '#0e3350',
                '--tem-accent': '#2ec5a2',
                '--tem-danger': '#d4544f',
                '--tem-warn': '#e0a13c',
                '--tem-success': '#2ea06b',
            }
        };
    }

    /** Carrega a paleta salva do usuário (null quando nunca configurou). */
    load(iduser: number): Promise<CustomThemePalette | null> {
        return new Promise((resolve) => {
            this.ucs.loadUserPreference(iduser, CUSTOM_THEME_PREFERENCE_ID).subscribe({
                next: (data) => {
                    let pref: Object[] = data['Mktpuserpref'];
                    if (ObjectUtil.isNotEmpty(pref) && pref.length > 0) {
                        // se o save gerar linhas duplicadas em vez de atualizar,
                        // usa a MAIS RECENTE (maior INTERNALID; senão, a última)
                        let row = pref[pref.length - 1];
                        if (pref.length > 1 && pref[0]['INTERNALID'] != null) {
                            row = pref.reduce((a, b) => Number(a['INTERNALID']) > Number(b['INTERNALID']) ? a : b);
                        }
                        try {
                            let palette = JSON.parse(row['PREFERENCE']) as CustomThemePalette;
                            if (palette && palette.colors) {
                                resolve(palette);
                                return;
                            }
                        } catch (e) {
                            console.error('Erro ao fazer parse da paleta do tema personalizado:', e);
                            console.error('Texto recebido:', row['PREFERENCE']);
                        }
                    }
                    resolve(null);
                },
                error: (error) => {
                    console.error('Erro ao carregar tema personalizado:', error);
                    resolve(null);
                }
            });
        });
    }

    /** Persiste a paleta do usuário. */
    save(iduser: number, palette: CustomThemePalette): Promise<any> {
        return this.ucs.saveUserResource(CUSTOM_THEME_PREFERENCE_ID, iduser, palette);
    }

    /** Carrega e aplica a paleta salva; retorna a paleta efetiva (padrão claro se nunca configurada). */
    async loadAndApply(iduser: number): Promise<CustomThemePalette> {
        let palette = await this.load(iduser);
        if (palette == null) {
            palette = this.defaults('light');
        }
        this.apply(palette);
        return palette;
    }

    /** (Re)gera o <style> com a classe .custom-theme a partir da paleta. */
    apply(palette: CustomThemePalette): void {
        if (palette == null || palette.colors == null) {
            return;
        }

        let declarations = this.tokens
            .filter(t => ObjectUtil.isNotEmpty(palette.colors[t.token]))
            .map(t => `  ${t.token}: ${this.sanitizeColor(palette.colors[t.token])};`)
            .join('\n');

        // derivados re-declarados para o color-mix re-resolver com as cores custom
        let shadow = palette.base === 'dark'
            ? '0 2px 10px rgba(0, 0, 0, 0.45)'
            : '0 2px 8px color-mix(in srgb, var(--tem-ink) 8%, transparent)';

        // Escopos:
        // - body.custom-theme: herança global (elementos fora do nz-layout)
        // - body.custom-theme .system-view-app / .cdk-overlay-container:
        //   declara os tokens NO elemento que carrega as classes de tema fixo
        //   (.vault-theme etc.) com especificidade maior — assim o custom (e o
        //   preview ao vivo) vence mesmo quando o tema ativo ainda é outro
        let scopes = 'body.custom-theme,\n'
            + 'body.custom-theme .system-view-app,\n'
            + 'body.custom-theme .cdk-overlay-container';

        let css = `${scopes} {\n${declarations}\n`
            + `  --tem-accent-soft: color-mix(in srgb, var(--tem-accent) 12%, transparent);\n`
            + `  --tem-focus-ring: 0 0 0 3px color-mix(in srgb, var(--tem-accent) 18%, transparent);\n`
            + `  --tem-shadow: ${shadow};\n`
            + `}`;

        this.getStyleTag().textContent = css;

        document.body.classList.add('custom-theme');
        document.documentElement.classList.remove('custom-theme');

        // componentes NG-Zorro sem override no temapi-theme seguem as
        // variáveis --ant-* — regenera a paleta a partir das cores custom
        this.nzBridge.syncColors({
            primary: palette.colors['--tem-deep'],
            success: palette.colors['--tem-success'],
            warning: palette.colors['--tem-warn'],
            error: palette.colors['--tem-danger'],
        });
    }

    /** Remove os overrides (usuário saiu do layout custom). */
    clear(): void {
        let tag = document.getElementById(STYLE_TAG_ID);
        if (tag != null) {
            tag.textContent = '';
        }
        document.body.classList.remove('custom-theme');
        document.documentElement.classList.remove('custom-theme');
    }

    private getStyleTag(): HTMLStyleElement {
        let tag = document.getElementById(STYLE_TAG_ID) as HTMLStyleElement;
        if (tag == null) {
            tag = document.createElement('style');
            tag.id = STYLE_TAG_ID;
            document.head.appendChild(tag);
        }
        return tag;
    }

    /** Aceita apenas cores hex (#rgb/#rrggbb/#rrggbbaa) — evita injeção de CSS arbitrário. */
    private sanitizeColor(value: string): string {
        let color = String(value).trim();
        return /^#([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(color) ? color : 'transparent';
    }
}
